import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, type Language } from '@up2d8/contracts';
import { cn } from '@up2d8/ui';
import { AdminIcon, ProductBrand, Segmented } from '../admin-kit';
import { setLanguage } from '../../shared/i18n';
import { useActiveBrand } from '../../shared/theme';
import './auth.css';

/**
 * De vorm van elk scherm buiten de schil (spec 01 T1-T10): links het merkvlak, rechts het
 * formulier van 400 breed met de taalkeuze rechtsboven.
 *
 * `.a-scope` op het buitenste element, zodat de klassen van `admin.css` hier ook gelden. Bij het
 * platform is het merkvlak donkerder (`.platform`): wie per ongeluk op het verkeerde aanmeldscherm
 * staat, ziet het voor hij iets invult.
 *
 * Het merkvlak draagt het opgeloste merk (MRK-2): het productlogo of het schild met de productnaam,
 * de kop en de tekst van het aanmeldscherm (anders de standaardtekst) en een eventuele
 * achtergrondafbeelding onder een donkere laag, zodat witte tekst leesbaar blijft. De platform-login
 * houdt zijn eigen kop: daar meldt geen klant zich aan.
 */
export function AuthFrame({ platform, children }: { platform?: boolean; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language.slice(0, 2) as Language;
  const brand = useActiveBrand();

  const claim = platform
    ? t('beheer.toegang.side.platformClaim')
    : (brand.loginTitle ?? t('beheer.toegang.side.claim'));
  const sub = platform
    ? t('beheer.toegang.side.platformSub')
    : (brand.loginText ?? t('beheer.toegang.side.sub'));
  const background = !platform && brand.loginBackgroundUrl ? brand.loginBackgroundUrl : null;
  const sideStyle: CSSProperties | undefined = background
    ? {
        backgroundImage: `linear-gradient(var(--a-auth-overlay), var(--a-auth-overlay)), url("${background.replace(/"/g, '%22')}")`,
      }
    : undefined;

  return (
    <div className="a-scope">
      <div className={cn('a-auth', platform && 'platform')}>
        <div
          className={cn('a-auth-side', background && 'has-background')}
          style={sideStyle}
          data-testid="auth-side"
        >
          <div className="a-auth-brand">
            <ProductBrand variant="auth" />
          </div>
          <div className="a-auth-claim" data-testid="auth-claim">
            {claim}
            <small>{sub}</small>
          </div>
          {/* Het echte adres: een klant met een eigen domein ziet het zijne. */}
          <div className="a-auth-host">{window.location.host}</div>
        </div>
        <main className="a-auth-main">
          <div className="a-auth-lang">
            <Segmented
              label={t('common.language')}
              value={language}
              onChange={setLanguage}
              options={supportedLanguages.map((value) => ({
                value,
                label: value.toUpperCase(),
              }))}
            />
          </div>
          <div className="a-auth-box">{children}</div>
        </main>
      </div>
    </div>
  );
}

/** De titel van een aanmeldscherm: de enige `h1`. */
export function AuthTitle({ children }: { children: ReactNode }) {
  return <h1 className="a-auth-title">{children}</h1>;
}

/**
 * Het ronde vlak boven een eindtoestand (T7 verlopen, T9 mail verstuurd): rood met een klok, groen
 * met een envelop of een vinkje. De kleuren staan als inline stijl, zoals in de mockup.
 */
export function AuthIcon({ tone, icon }: { tone: 'danger' | 'success'; icon: ReactNode }) {
  return (
    <div
      className="a-empty-icon"
      style={
        tone === 'danger'
          ? { background: 'var(--danger-tint)', color: 'var(--a-danger-text)' }
          : { background: 'var(--success-tint)', color: 'var(--a-success-text)' }
      }
    >
      {icon}
    </div>
  );
}

/** De klok van T7 (26 px), met de paden uit de mockup. */
export function ClockIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

/** De envelop van T9 (26 px), met de paden uit de mockup. */
export function MailIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

/** Een vinkje van 26 px voor "opgeslagen" (niet in de mockup; zelfde vorm als T9). */
export function DoneIcon() {
  return <AdminIcon name="checkCircle" size={26} />;
}
